import CLASSNAME from "./classname";
import { useDispatch, useSelector } from "react-redux";
import {
  initializeAudio,
  toggleSpeaker,
  setAudioSource,
} from "../../services/audioContext";
import { RootState } from "../../../../store";
import { useEffect, useRef, useState } from "react";
import { toggleSpeaker as toggleSpeakerAction } from "../../../../store/reducers/livestreamSettings";
// import { HiChevronDown } from "react-icons/hi";
import AudioOutputSelector from "../audioOutputSelector";
import { RiSoundModuleLine } from "react-icons/ri";

interface SoundSettingsProps {
  audioSourceUrl: string;
}

const SoundSettings = ({ audioSourceUrl }: SoundSettingsProps) => {
  const dispatch = useDispatch();
  const speakerState = useSelector(
    (state: RootState) => state.live.settings.onSpeaker
  );
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isAudioMenuOpen, setIsAudioMenuOpen] = useState<boolean>(false);
  const [selectedAudioOutput, setSelectedAudioOutput] = useState<string>('');
  const menuRef = useRef<HTMLDivElement>(null); // For outside click detection

  const handleAudioOutputChange = (deviceId: string) => {
    setSelectedAudioOutput(deviceId);
    if (videoRef.current) {
      // @ts-ignore
      videoRef.current.setSinkId(deviceId).catch(err => console.error('Error setting audio output:', err));
    }
    setIsAudioMenuOpen(false);
  };

  const [audioOutputDevices, setAudioOutputDevices] = useState<MediaDeviceInfo[]>([]);

  useEffect(() => {
    const fetchAudioDevices = async () => {
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioOutputs = devices.filter(device => device.kind === 'audiooutput');
        setAudioOutputDevices(audioOutputs);
        if (audioOutputs.length > 0) {
          // Prioritize Bluetooth device
          const bluetoothDevice = audioOutputs.find(device =>
            device.label.toLowerCase().includes('bluetooth') ||
            device.label.toLowerCase().includes('wireless') ||
            device.label.toLowerCase().includes('headset')
          );
          const selectedDeviceId = bluetoothDevice ? bluetoothDevice.deviceId : audioOutputs[0].deviceId;
          setSelectedAudioOutput(selectedDeviceId);
          if (videoRef.current) {
            // @ts-ignore
            videoRef.current.setSinkId(selectedDeviceId).catch(err => console.error('Error setting audio output:', err));
          }
        }
      } catch (error) {
        console.error('Error fetching audio devices:', error);
      }
    };

    fetchAudioDevices();

    const handleDeviceChange = () => {
      fetchAudioDevices();
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);

    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
    };
  }, []);

  useEffect(() => {
    initializeAudio();
    setAudioSource(audioSourceUrl);
  }, [audioSourceUrl]);

  // Combined handler: Toggle speaker and open menu (adjust if you want menu toggle separate)
  const handleClick = () => {
    const newSpeakerState = !speakerState;
    dispatch(toggleSpeakerAction());
    toggleSpeaker(newSpeakerState);
    setIsAudioMenuOpen(true); // Always open menu on click; use !isAudioMenuOpen if toggle desired
  };

  // Outside click handler
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsAudioMenuOpen(false);
      }
    };

    if (isAudioMenuOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isAudioMenuOpen]);

  return (
    <div className="relative group">
      <div
        ref={menuRef} // Attach ref for outside click
        className={`${CLASSNAME} ${!speakerState && "bg-red-600 rounded-full"}`}
        style={{
          background: "rgb(95, 72, 138)",
          boxShadow:
            "rgb(152, 122, 209) -8px -8px 16px inset, rgba(2, 2, 2, 0.61) 8px 8px 8px inset",
        }}
        onClick={handleClick}
      >
        <>
          <RiSoundModuleLine color="white" />
          {/* <HiChevronDown color="white" style={{ fontSize: '20px' }} /> */}
        </>
      </div>
      {isAudioMenuOpen && (
              <AudioOutputSelector
              showTrigger={false}
              open={isAudioMenuOpen}
              onOpenChange={setIsAudioMenuOpen}
            />
              )}
      <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 max-w-md bg-black text-white text-sm rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        Sound
      </div>
    </div>
  );
};

export default SoundSettings;